import React from "react";
import ReplyPreview from "./ReplyPreview";
import VoicePlayer from "./VoicePlayer";
import { REACTION_EMOJIS } from "./constants";
import { formatTime } from "./chatUtils";

// =====================================================
// MESSAGE BUBBLE
// Renders a single message of any kind — text, image, video, voice, file —
// plus its reply quote, "forwarded"/"edited" labels, reactions, the delivery
// tick (mine only) and the quick-reaction picker. Purely presentational: all
// behaviour comes in through callbacks, so the container keeps owning state.
//
// Memoised so a re-render of the conversation only touches bubbles whose props
// actually changed — important for long threads.
// =====================================================

// Collapse the reactions array into unique emojis with a count.
const summarizeReactions = (reactions = []) => {
  const map = new Map();
  for (const r of reactions) {
    if (!r?.emoji) continue;
    map.set(r.emoji, (map.get(r.emoji) || 0) + 1);
  }
  return [...map.entries()].map(([emoji, count]) => ({ emoji, count }));
};

function MessageBubble({
  msg,
  mine,
  highlighted,
  registerRef,
  reactionPickerOpen,
  onContextMenu,
  onToggleReactionPicker,
  onReact,
  onReplyJump,
  onRetry,
}) {
  const reactions = summarizeReactions(msg.reactions);

  const renderBody = () => {
    if (msg.fileUrl) {
      if (msg.fileType?.startsWith("image")) {
        return <img src={msg.fileUrl} alt="" className="msg-image" />;
      }
      if (msg.fileType?.startsWith("video")) {
        return <video src={msg.fileUrl} controls className="msg-video" />;
      }
      if (msg.fileType?.startsWith("audio")) {
        return (
          <VoicePlayer src={msg.fileUrl} duration={msg.duration} mine={mine} />
        );
      }
      return (
        <a
          href={msg.fileUrl}
          target="_blank"
          rel="noreferrer"
          className="msg-file"
        >
          📎 {msg.fileName || "File"}
        </a>
      );
    }
    return <span className="msg-text">{msg.text}</span>;
  };

  return (
    <div
      ref={registerRef}
      className={[
        mine ? "my-msg" : "their-msg",
        mine && msg.status === "failed" ? "failed-msg" : "",
        highlighted ? "msg-highlight" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onContextMenu={(e) => onContextMenu(e, msg)}
      onDoubleClick={() => onToggleReactionPicker(msg._id)}
    >
      {msg.forwarded && <div className="msg-forwarded">↪ Forwarded</div>}

      {msg.replyTo?.messageId && (
        <ReplyPreview
          reply={msg.replyTo}
          compact
          onClick={() => onReplyJump(msg.replyTo.messageId)}
        />
      )}

      {renderBody()}

      {reactions.length > 0 && (
        <div className="message-reactions">
          {reactions.map((r) => (
            <span key={r.emoji} className="reaction-chip">
              {r.emoji}
              {r.count > 1 ? <small>{r.count}</small> : null}
            </span>
          ))}
        </div>
      )}

      <small>
        {msg.edited && <span className="msg-edited">edited</span>}
        {formatTime(msg.createdAt)}
        {mine &&
          (msg.status === "failed" ? (
            <span
              className="tick failed"
              title={msg.failReason || "Not sent — tap to retry"}
              onClick={() => onRetry(msg)}
              style={{ cursor: "pointer" }}
            >
              ⚠ retry
            </span>
          ) : msg.status === "sending" ? (
            <span className="tick sending" title="Sending…">
              🕓
            </span>
          ) : (
            <span className={`tick ${msg.status || "sent"}`}>
              {msg.status === "read" || msg.status === "delivered"
                ? "✓✓"
                : "✓"}
            </span>
          ))}
      </small>

      {reactionPickerOpen && (
        <div className="reaction-picker">
          {REACTION_EMOJIS.map((emoji) => (
            <span key={emoji} onClick={() => onReact(msg._id, emoji)}>
              {emoji}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default React.memo(MessageBubble);
